// src/modules/invoices/pages/InvoicePrintView.tsx

import { useEffect, useState, useRef } from "react";
import { getTenantSettings, TenantSettings } from "../../../services/settings";
import { useParams, useNavigate } from "react-router-dom";
import api from "../../../services/api";
import { useAuth } from "../../../context/AuthContext";
import { useProtectedRoute } from "../../../hooks/useProtectedRoute";
import InvoicePDFDownload from "./InvoicePDFDownload";

type LineItem = {
  category: string;
  name: string;
  description: string;
  quantity: number;
  costPrice: number;
  sellingPrice: number;
  taxRate: number;
  discount: number;
  profit: number;
  lineTotal: number;
};

type Payment = {
  method: string;
  amount: number;
  date: string;
};

type SendHistoryEntry = {
  method: "email" | "whatsapp";
  date: string;
};

type InvoiceData = {
  _id: string;
  invoiceNumber: number;
  client: {
    fullName: string;
    phone: string;
    email: string;
  };
  shop: {
    tenantName: string;
    tenantPhone: string;
    tenantEmail: string;
    logoURL?: string;
    address?: string;
    taxId?: string;
  };
  ticket?: {
    ticketNumber: number;
    jobType: string;
    deviceType: string;
    brand: string;
    model: string;
  } | null;
  items: LineItem[];
  subtotal: number;
  taxTotal: number;
  discountTotal: number;
  depositTotal: number;
  grandTotal: number;
  paymentStatus: "DRAFT" | "UNPAID" | "PARTIALLY PAID" | "PAID";
  payments: Payment[];
  notes: string;
  sendHistory: SendHistoryEntry[];
  createdAt: string;
  updatedAt: string;
};

export default function InvoicePrintView() {
  useProtectedRoute();
  const { token } = useAuth();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const containerRef = useRef<HTMLDivElement | null>(null);

  const [invoice, setInvoice] = useState<InvoiceData | null>(null);
  const [template, setTemplate] = useState<'classic' | 'compact'>("classic");
  const [branding, setBranding] = useState<{ logoLightUrl?: string } | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInvoice = async () => {
      try {
        const res = await api.get<{ invoice: InvoiceData }>(
          `/invoices/${id}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        setInvoice(res.data.invoice);
      } catch {
        alert("Failed to load invoice");
        navigate("/invoices");
      } finally {
        setLoading(false);
      }
    };
    fetchInvoice();
  }, [id, token, navigate]);

  useEffect(() => {
    (async () => {
      if (!token) return;
      try {
        const s: TenantSettings = await getTenantSettings(token);
        setTemplate((s.templates?.invoiceTemplate as any) || "classic");
        setBranding({ logoLightUrl: s.branding?.logoLightUrl || undefined });
      } catch {}
    })();
  }, [token]);

  if (loading || !invoice) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">Loading invoice…</p>
      </div>
    );
  }

  const {
    invoiceNumber,
    client,
    shop,
    ticket,
    items,
    subtotal,
    taxTotal,
    discountTotal,
    depositTotal,
    grandTotal,
    paymentStatus,
    payments,
    notes,
    sendHistory,
    createdAt,
  } = invoice;

  return (
    <div className="min-h-screen bg-gray-100 pb-10">
      {/* Top bar with navigation/print/download (hidden when actually printing) */}
      <div className="bg-white shadow px-6 py-4 flex flex-col md:flex-row items-start md:items-center justify-between sticky top-0 z-10 print:hidden">
        <div className="mb-4 md:mb-0">
          <h2 className="text-2xl font-bold text-blue-700">
            Invoice #{invoiceNumber}
          </h2>
          <p className="text-sm text-gray-500">
            Created: {new Date(createdAt).toLocaleDateString()}
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => window.print()}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
          >
            Print
          </button>
          <InvoicePDFDownload
            elementId="invoice-content"
            fileName={`invoice-${invoiceNumber}`}
          />
          <button
            onClick={() => navigate("/invoices")}
            className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition"
          >
            Back to List
          </button>
        </div>
      </div>

      {/* Actual invoice contents */}
      <div
        id="invoice-content"
        ref={containerRef}
        className={`max-w-4xl mx-auto bg-white rounded-lg shadow-lg ${template === 'compact' ? 'p-4' : 'p-8'} mt-6 print:mt-0`}
      >
        {/* SHOP HEADER */}
        <div className="flex flex-col md:flex-row justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-blue-700">{shop.tenantName}</h1>
            {shop.address && (
              <p className="text-gray-600">{shop.address}</p>
            )}
            <p className="text-gray-600">Phone: {shop.tenantPhone}</p>
            <p className="text-gray-600">Email: {shop.tenantEmail}</p>
            {shop.taxId && (
              <p className="text-gray-600">Tax ID: {shop.taxId}</p>
            )}
          </div>
          {(branding?.logoLightUrl || shop.logoURL) && (
            <div className="mt-4 md:mt-0">
              <img
                src={branding?.logoLightUrl || shop.logoURL}
                alt="Shop Logo"
                className="h-20 object-contain"
              />
            </div>
          )}
        </div>

        {/* CLIENT + TICKET INFO */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Client */}
          <div>
            <h2 className="text-xl font-semibold text-gray-700 mb-2">
              Bill To:
            </h2>
            <p className="text-gray-800 font-medium">{client.fullName}</p>
            <p className="text-gray-600">Phone: {client.phone}</p>
            <p className="text-gray-600">Email: {client.email}</p>
          </div>

          {/* Ticket (if linked) */}
          {ticket && (
            <div>
              <h2 className="text-xl font-semibold text-gray-700 mb-2">
                Job Ticket:
              </h2>
              <p className="text-gray-800 font-medium">
                #{ticket.ticketNumber} &mdash; {ticket.jobType}
              </p>
              <p className="text-gray-600">
                Device: {ticket.deviceType} / {ticket.brand} / {ticket.model}
              </p>
            </div>
          )}
        </div>

        {/* LINE ITEMS TABLE */}
        <div className="overflow-x-auto mb-8">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-gray-200">
                <th className="border px-4 py-2 text-left text-gray-700">Category</th>
                <th className="border px-4 py-2 text-left text-gray-700">Name</th>
                <th className="border px-4 py-2 text-left text-gray-700">Description</th>
                <th className="border px-4 py-2 text-right text-gray-700">Qty</th>
                <th className="border px-4 py-2 text-right text-gray-700">Unit Price</th>
                <th className="border px-4 py-2 text-right text-gray-700">Tax %</th>
                <th className="border px-4 py-2 text-right text-gray-700">Discount</th>
                <th className="border px-4 py-2 text-right text-gray-700">Line Total</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item, idx) => (
                <tr
                  key={idx}
                  className={idx % 2 === 0 ? "bg-white" : "bg-gray-50"}
                >
                  <td className="border px-4 py-2 text-gray-800">{item.category}</td>
                  <td className="border px-4 py-2 text-gray-800">{item.name}</td>
                  <td className="border px-4 py-2 text-gray-600">
                    {item.description || "-"}
                  </td>
                  <td className="border px-4 py-2 text-right text-gray-800">
                    {item.quantity}
                  </td>
                  <td className="border px-4 py-2 text-right text-gray-800">
                    ${item.sellingPrice.toFixed(2)}
                  </td>
                  <td className="border px-4 py-2 text-right text-gray-800">
                    {item.taxRate}%
                  </td>
                  <td className="border px-4 py-2 text-right text-gray-800">
                    ${item.discount.toFixed(2)}
                  </td>
                  <td className="border px-4 py-2 text-right text-gray-800">
                    ${item.lineTotal.toFixed(2)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* NOTES */}
        {notes && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-1">Notes:</h3>
            <p className="text-gray-600 whitespace-pre-wrap">{notes}</p>
          </div>
        )}

        {/* PAYMENTS (if any) */}
        {payments.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-gray-700 mb-2">Payments:</h3>
            <ul className="list-disc list-inside space-y-1">
              {payments.map((pmt, idx) => (
                <li key={idx} className="text-gray-600">
                  {new Date(pmt.date).toLocaleDateString()} – {pmt.method} – $
                  {pmt.amount.toFixed(2)}
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* SUMMARY BOX */}
        <div className="w-full md:w-2/3 lg:w-1/2 ml-auto bg-gray-50 border rounded-lg shadow-sm p-6">
          <div className="flex justify-between mb-2">
            <span className="text-gray-700">Subtotal:</span>
            <span className="text-gray-800 font-medium">
              ${subtotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-700">Tax Total:</span>
            <span className="text-gray-800 font-medium">
              ${taxTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-700">Discount Total:</span>
            <span className="text-gray-800 font-medium">
              - ${discountTotal.toFixed(2)}
            </span>
          </div>
          <div className="flex justify-between mb-2">
            <span className="text-gray-700">Deposit Applied:</span>
            <span className="text-gray-800 font-medium">
              - ${depositTotal.toFixed(2)}
            </span>
          </div>
          <div className="border-t border-gray-300 my-4"></div>
          <div className="flex justify-between font-semibold text-xl">
            <span className="text-gray-800">Grand Total:</span>
            <span className="text-gray-900">${grandTotal.toFixed(2)}</span>
          </div>
        </div>

        {/* FOOTER */}
        <div className="mt-10 text-center text-sm text-gray-500">
          <p>Payment Status: <span className="font-medium">{paymentStatus}</span></p>
          {sendHistory.length > 0 && (
            <p className="mt-2">
              <span className="font-medium">Sent via:</span>{" "}
              {sendHistory
                .map((s) => `${s.method} on ${new Date(s.date).toLocaleDateString()}`)
                .join(", ")}
            </p>
          )}
          <p className="mt-6">Thank you for your business!</p>
          <p>Generated by RepairSync</p>
        </div>
      </div>
    </div>
  );
}
